import { useState, useRef, useEffect, useMemo } from "react";
import { useAuth } from "@clerk/react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useListCategories } from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Plus, Pencil, Trash2, Upload, X, AlertCircle, Search, ChevronUp, ChevronDown, ChevronsUpDown, ClipboardPaste, CheckCircle2 } from "lucide-react";
import AdminLayout from "./AdminLayout";

const DISTRICTS = [
  "中西區","灣仔區","東區","南區","油尖旺區","深水埗區","九龍城區",
  "黃大仙區","觀塘區","葵青區","荃灣區","屯門區","元朗區","北區",
  "大埔區","沙田區","西貢區","離島區"
];

interface Tender {
  id: number; title: string; district: string; status: string;
  deadlineDate: string; deadlineTime?: string; description?: string; schoolName?: string;
  contactInfo?: string; contactAddress?: string; contactPerson?: string;
  contactPhone?: string; contactEmail?: string;
  referenceUrl?: string | null;
  documentUrls: string[];
  categories: { slug: string; nameChinese: string }[];
  createdAt?: string;
}

interface SchoolSuggestion {
  id: number; name: string; district: string;
  address: string | null; phone: string | null; email: string | null;
  contactPerson: string | null;
  tenderUrls?: string[];
}

const EMPTY_DOC_URLS = (): string[] => Array(10).fill("");

const empty = (): Omit<Tender, "id" | "categories"> & { categories: string[] } => ({
  title: "", district: DISTRICTS[0], status: "open", deadlineDate: "", deadlineTime: "",
  description: "", schoolName: "", contactInfo: "",
  contactAddress: "", contactPerson: "", contactPhone: "", contactEmail: "",
  referenceUrl: "",
  documentUrls: EMPTY_DOC_URLS(), categories: [],
});

async function adminFetch(path: string, init?: RequestInit, token?: string | null) {
  const res = await fetch(path, {
    ...init,
    headers: { ...(init?.headers ?? {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error ?? res.statusText);
  return res.json();
}

function SchoolSearch({ value, onSelect }: {
  value: string;
  onSelect: (s: SchoolSuggestion) => void;
}) {
  const [q, setQ] = useState(value);
  const [open, setOpen] = useState(false);
  const [dq, setDq] = useState("");
  const dropRef = useRef<HTMLDivElement>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => { setQ(value); }, [value]);

  useEffect(() => {
    clearTimeout(timer.current);
    if (q.length >= 1) { timer.current = setTimeout(() => setDq(q), 300); }
    else setDq("");
  }, [q]);

  const { getToken } = useAuth();
  const { data: suggestions = [] } = useQuery<SchoolSuggestion[]>({
    queryKey: ["school-search", dq],
    queryFn: async () => adminFetch(`/api/admin/schools/search?q=${encodeURIComponent(dq)}`, undefined, await getToken()),
    enabled: dq.length >= 1,
  });

  useEffect(() => {
    function close(e: MouseEvent) {
      if (dropRef.current && !dropRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  return (
    <div className="relative" ref={dropRef}>
      <div className="relative">
        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          value={q}
          onChange={e => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="輸入學校名稱搜尋…"
          className="pl-8"
        />
      </div>
      {open && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-md max-h-52 overflow-y-auto">
          {suggestions.map(s => (
            <button
              key={s.id}
              className="w-full text-left px-3 py-2.5 text-sm hover:bg-accent border-b last:border-0"
              onMouseDown={e => e.preventDefault()}
              onClick={() => { setQ(s.name); onSelect(s); setOpen(false); }}
            >
              <div className="font-medium">{s.name}</div>
              <div className="text-xs text-muted-foreground">{s.district}{s.address ? ` · ${s.address}` : ""}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const TIME_OPTIONS = Array.from({ length: 48 }, (_, i) => {
  const h = String(Math.floor(i / 2)).padStart(2, "0");
  const m = i % 2 === 0 ? "00" : "30";
  return `${h}:${m}`;
});

function parseSmartDate(raw: string): string {
  const digits = raw.replace(/\D/g, "");
  if (digits.length === 8) return `${digits.slice(0,4)}-${digits.slice(4,6)}-${digits.slice(6,8)}`;
  return raw;
}

function parseSmartTime(raw: string): string {
  const digits = raw.replace(/\D/g, "");
  if (digits.length === 3) return `0${digits[0]}:${digits.slice(1)}`;
  if (digits.length === 4) return `${digits.slice(0,2)}:${digits.slice(2)}`;
  return raw;
}

function SmartDateInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [raw, setRaw] = useState(value);
  useEffect(() => { setRaw(value); }, [value]);

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const v = e.target.value;
    const digits = v.replace(/\D/g, "");
    if (digits.length === 8 && v === digits) {
      const formatted = parseSmartDate(v);
      setRaw(formatted);
      onChange(formatted);
    } else {
      setRaw(v);
      onChange(v);
    }
  }

  function handleBlur() {
    const formatted = parseSmartDate(raw);
    setRaw(formatted);
    onChange(formatted);
  }

  return (
    <Input
      value={raw}
      onChange={handleChange}
      onBlur={handleBlur}
      placeholder="20260102"
      maxLength={10}
    />
  );
}

function SmartTimeInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [raw, setRaw] = useState(value);
  const [showDrop, setShowDrop] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  useEffect(() => { setRaw(value); }, [value]);

  function commit(v: string) {
    const parsed = parseSmartTime(v);
    setRaw(parsed);
    onChange(parsed);
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const v = e.target.value;
    const digits = v.replace(/\D/g, "");
    // Only auto-format when exactly 4 digits typed (e.g. "1330"), not 3
    if (digits.length === 4 && v === digits) {
      commit(v);
    } else {
      setRaw(v);
      onChange(v);
    }
  }

  useEffect(() => {
    function close(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setShowDrop(false);
    }
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  return (
    <div className="relative" ref={wrapRef}>
      <Input
        autoComplete="off"
        value={raw}
        onChange={handleChange}
        onFocus={() => setShowDrop(true)}
        onBlur={() => { commit(raw); setShowDrop(false); }}
        onKeyDown={e => {
          if (e.key === "Enter" || e.key === "Escape") {
            e.preventDefault();
            commit(raw);
            setShowDrop(false);
            (e.target as HTMLInputElement).blur();
          }
        }}
        placeholder="1330"
        maxLength={5}
      />
      {showDrop && (
        <div className="absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-md max-h-48 overflow-y-auto">
          {TIME_OPTIONS.map(t => (
            <button
              key={t}
              tabIndex={-1}
              className="w-full text-left px-3 py-1.5 text-sm hover:bg-accent"
              onMouseDown={e => e.preventDefault()}
              onClick={() => { setRaw(t); onChange(t); setShowDrop(false); }}
            >{t}</button>
          ))}
        </div>
      )}
    </div>
  );
}

type SortCol = "id" | "title" | "schoolName" | "district" | "status" | "deadlineDate";
type SortDir = "asc" | "desc";

function SortIcon({ col, sortCol, sortDir }: { col: SortCol; sortCol: SortCol; sortDir: SortDir }) {
  if (col !== sortCol) return <ChevronsUpDown className="h-3.5 w-3.5 ml-1 opacity-40" />;
  return sortDir === "asc"
    ? <ChevronUp className="h-3.5 w-3.5 ml-1 text-primary" />
    : <ChevronDown className="h-3.5 w-3.5 ml-1 text-primary" />;
}

interface JsonImportResult { inserted: number; errors: string[]; total: number; }

export default function AdminTendersPage() {
  const qc = useQueryClient();
  const { getToken } = useAuth();
  const af = async (path: string, init?: RequestInit) => adminFetch(path, init, await getToken());
  const { data: categories } = useListCategories();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Tender | null>(null);
  const [form, setForm] = useState(empty());
  const [importResult, setImportResult] = useState<{ inserted: number; errors: string[] } | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const [searchSchool, setSearchSchool] = useState("");
  const [searchTitle, setSearchTitle] = useState("");
  const [searchCategory, setSearchCategory] = useState("");
  const [sortCol, setSortCol] = useState<SortCol>("id");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const saveMode = useRef<"save" | "saveAndNext">("save");
  const pendingNextTender = useRef<Tender | null>(null);

  // JSON 匯入
  const [jsonDialogOpen, setJsonDialogOpen] = useState(false);
  const [jsonText, setJsonText] = useState("");
  const [jsonParsed, setJsonParsed] = useState<unknown[] | null>(null);
  const [jsonRaw, setJsonRaw] = useState<unknown>(null); // 原始解析物件（含 metadata）
  const [jsonMeta, setJsonMeta] = useState<{
    schoolName?: string; schoolNo?: string; tenderUrl?: string;
    exportedAt?: string; total?: number;
    // 多學校模式
    isMultiSchool?: boolean; totalSchools?: number; totalNew?: number;
    schoolSummary?: { name: string; count: number }[];
  } | null>(null);
  const [jsonParseError, setJsonParseError] = useState<string | null>(null);
  const [jsonImporting, setJsonImporting] = useState(false);
  const [jsonResult, setJsonResult] = useState<JsonImportResult | null>(null);

  function handleJsonTextChange(text: string) {
    setJsonText(text);
    setJsonResult(null);
    if (!text.trim()) { setJsonParsed(null); setJsonParseError(null); setJsonMeta(null); setJsonRaw(null); return; }
    try {
      const parsed = JSON.parse(text.trim());
      setJsonRaw(parsed);

      // ① 直接陣列
      if (Array.isArray(parsed)) {
        setJsonParsed(parsed);
        setJsonMeta(null);
        setJsonParseError(null);
        return;
      }

      if (parsed && typeof parsed === "object") {
        // ② 多學校格式：{ schools: [{ school_name, items: [...] }] }
        if (Array.isArray(parsed.schools)) {
          const schools = parsed.schools as any[];
          const allItems = schools.flatMap((s: any) => s.items ?? []);
          setJsonParsed(allItems);
          const summary = schools
            .filter((s: any) => (s.items?.length ?? 0) > 0)
            .map((s: any) => ({ name: s.school_name ?? "未知學校", count: s.items.length }));
          setJsonMeta({
            isMultiSchool: true,
            totalSchools: schools.length,
            totalNew: typeof parsed.total_new === "number" ? parsed.total_new : allItems.length,
            exportedAt: parsed.exported_at,
            schoolSummary: summary,
          });
          setJsonParseError(null);
          return;
        }

        // ③ 單學校爬蟲格式：{ records / items, school_name, ... }
        const arr = parsed.records ?? parsed.items ?? null;
        if (!Array.isArray(arr)) {
          setJsonParsed(null);
          setJsonMeta(null);
          setJsonParseError("JSON 必須為陣列，或含 records / items / schools 欄位的物件");
          return;
        }
        setJsonParsed(arr);
        const meta: NonNullable<typeof jsonMeta> = {};
        if (parsed.school_name) meta.schoolName = parsed.school_name;
        if (parsed.school_no) meta.schoolNo = parsed.school_no;
        if (parsed.tender_url) meta.tenderUrl = parsed.tender_url;
        if (parsed.exported_at) meta.exportedAt = parsed.exported_at;
        if (typeof parsed.total === "number") meta.total = parsed.total;
        setJsonMeta(Object.keys(meta).length > 0 ? meta : null);
        setJsonParseError(null);
        return;
      }

      setJsonParsed(null); setJsonMeta(null);
      setJsonParseError("JSON 格式無效");
    } catch (e: any) {
      setJsonParsed(null); setJsonMeta(null); setJsonRaw(null);
      setJsonParseError("JSON 語法錯誤：" + e.message);
    }
  }

  async function handleJsonImport() {
    if (!jsonParsed?.length || !jsonRaw) return;
    setJsonImporting(true);
    try {
      // 傳送原始物件（後端自動識別 records / items / 陣列）
      const data = await af("/api/admin/tenders/import-json", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(jsonRaw),
      });
      if (!data) throw new Error("匯入失敗");
      setJsonResult(data);
      qc.invalidateQueries({ queryKey: ["admin-tenders"] });
    } catch (e: any) {
      setJsonResult({ inserted: 0, errors: [e.message], total: jsonParsed.length });
    } finally {
      setJsonImporting(false);
    }
  }

  function closeJsonDialog() {
    setJsonDialogOpen(false);
    setJsonText("");
    setJsonParsed(null);
    setJsonRaw(null);
    setJsonMeta(null);
    setJsonParseError(null);
    setJsonResult(null);
    setJsonImporting(false);
  }

  const { data: tenders = [], isLoading, isError, error } = useQuery<Tender[]>({
    queryKey: ["admin-tenders"],
    queryFn: () => af("/api/admin/tenders"),
  });

  function handleSort(col: SortCol) {
    if (col === sortCol) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortCol(col); setSortDir("asc"); }
  }

  const displayedTenders = useMemo(() => {
    let list = [...tenders];
    if (searchSchool.trim()) {
      const raw = searchSchool.trim();
      const isExact = raw.startsWith("[") && raw.endsWith("]");
      if (isExact) {
        const q = raw.slice(1, -1).toLowerCase();
        list = list.filter(t => t.schoolName?.toLowerCase() === q);
      } else {
        const q = raw.toLowerCase();
        list = list.filter(t => t.schoolName?.toLowerCase().includes(q));
      }
    }
    if (searchTitle.trim()) {
      const q = searchTitle.trim().toLowerCase();
      list = list.filter(t => t.title?.toLowerCase().includes(q));
    }
    if (searchCategory) {
      list = list.filter(t => t.categories.some(c => c.slug === searchCategory));
    }
    list.sort((a, b) => {
      let av: string | number = "", bv: string | number = "";
      if (sortCol === "id") { av = a.id; bv = b.id; }
      else if (sortCol === "title") { av = a.title; bv = b.title; }
      else if (sortCol === "schoolName") { av = a.schoolName ?? ""; bv = b.schoolName ?? ""; }
      else if (sortCol === "district") { av = a.district; bv = b.district; }
      else if (sortCol === "status") { av = a.status; bv = b.status; }
      else if (sortCol === "deadlineDate") { av = a.deadlineDate; bv = b.deadlineDate; }
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return list;
  }, [tenders, searchSchool, searchTitle, searchCategory, sortCol, sortDir]);

  const save = useMutation({
    mutationFn: (data: typeof form & { id?: number }) => {
      const { id, ...body } = data;
      return af(id ? `/api/admin/tenders/${id}` : "/api/admin/tenders", {
        method: id ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-tenders"] });
      if (saveMode.current === "saveAndNext" && pendingNextTender.current) {
        openEdit(pendingNextTender.current);
      } else {
        setOpen(false);
      }
      saveMode.current = "save";
      pendingNextTender.current = null;
    },
  });

  const remove = useMutation({
    mutationFn: (id: number) => af(`/api/admin/tenders/${id}`, { method: "DELETE" }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin-tenders"] }); setDeleteId(null); },
  });

  function getNextTender(): Tender | null {
    if (!editing) return null;
    const idx = displayedTenders.findIndex(t => t.id === editing.id);
    if (idx === -1 || idx >= displayedTenders.length - 1) return null;
    return displayedTenders[idx + 1];
  }

  function handleSaveAndNext() {
    const next = getNextTender();
    saveMode.current = "saveAndNext";
    pendingNextTender.current = next;
    save.mutate({ ...form, ...(editing ? { id: editing.id } : {}) });
  }

  function openAdd() { setForm(empty()); setEditing(null); setOpen(true); }
  function openEdit(t: Tender) {
    setEditing(t);
    setForm({
      ...t,
      categories: t.categories.map(c => c.slug),
      description: t.description ?? "",
      schoolName: t.schoolName ?? "",
      contactInfo: t.contactInfo ?? "",
      contactAddress: t.contactAddress ?? "",
      contactPerson: t.contactPerson ?? "",
      contactPhone: t.contactPhone ?? "",
      contactEmail: t.contactEmail ?? "",
      referenceUrl: t.referenceUrl ?? "",
      documentUrls: (() => {
        const filled = (t.documentUrls ?? []).slice(0, 10);
        return [...filled, ...Array(10 - filled.length).fill("")];
      })(),
      deadlineTime: t.deadlineTime ?? "",
    });
    setOpen(true);
  }

  function handleSchoolSelect(s: SchoolSuggestion) {
    setForm(f => ({
      ...f,
      schoolName: s.name,
      district: DISTRICTS.includes(s.district) ? s.district : f.district,
      contactAddress: f.contactAddress || s.address || "",
      contactPerson: f.contactPerson || s.contactPerson || "",
      contactPhone: f.contactPhone || s.phone || "",
      contactEmail: f.contactEmail || s.email || "",
      referenceUrl: s.tenderUrls?.length === 1 ? s.tenderUrls[0] : "",
    }));
  }

  function toggleCat(slug: string) {
    setForm(f => ({ ...f, categories: f.categories.includes(slug) ? f.categories.filter(s => s !== slug) : [...f.categories, slug] }));
  }

  function downloadTemplate() {
    const header = "title,district,status,deadlineDate,schoolName,description,contactInfo,contactAddress,contactPerson,contactPhone,contactEmail,referenceUrl,documentUrl,categories";
    const example = "學校冷氣保養,荃灣區,open,2026-06-30,荃灣某中學,年度冷氣機保養合約,2345-6789,荃灣某路1號,,2345-6789,info@school.edu.hk,https://school.edu.hk/tender/123,https://school.edu.hk/tender/123.pdf,engineering";
    const blob = new Blob([header + "\n" + example], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "tenders_template.csv"; a.click();
  }

  async function handleImport(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    const result = await af("/api/admin/tenders/import", { method: "POST", body: fd });
    setImportResult(result);
    qc.invalidateQueries({ queryKey: ["admin-tenders"] });
    if (fileRef.current) fileRef.current.value = "";
  }

  return (
    <AdminLayout tab="tenders">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold">招標管理</h2>
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" className="text-muted-foreground" onClick={downloadTemplate}>下載範本</Button>
          <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()}>
            <Upload className="h-4 w-4 mr-1.5" /> 匯入 CSV
          </Button>
          <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={handleImport} />
          <Button variant="outline" size="sm" onClick={() => setJsonDialogOpen(true)}>
            <ClipboardPaste className="h-4 w-4 mr-1.5" /> 貼上 JSON
          </Button>
          <Button size="sm" onClick={openAdd}><Plus className="h-4 w-4 mr-1.5" /> 新增招標</Button>
        </div>
      </div>

      {importResult && (
        <Card className={`mb-4 ${importResult.errors.length > 0 ? "border-amber-300" : "border-green-300"}`}>
          <CardContent className="p-4 text-sm flex items-start gap-2">
            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
            <div>
              <p>成功匯入 {importResult.inserted} 筆</p>
              {importResult.errors.length > 0 && <p className="text-amber-700 mt-1">錯誤：{importResult.errors.slice(0,3).join("；")}</p>}
            </div>
            <button className="ml-auto" onClick={() => setImportResult(null)}><X className="h-4 w-4" /></button>
          </CardContent>
        </Card>
      )}

      {/* 搜尋欄 */}
      <div className="flex flex-col gap-2 mb-3">
        {/* 第一行：文字搜尋 */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative w-80">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              value={searchSchool}
              onChange={e => setSearchSchool(e.target.value)}
              placeholder="學校名稱（[名稱] 完全符合）"
              className="pl-8 pr-7"
            />
            {searchSchool && (
              <button onClick={() => setSearchSchool("")} className="absolute right-2.5 top-2.5 text-muted-foreground hover:text-foreground">
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <div className="relative w-52">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              value={searchTitle}
              onChange={e => setSearchTitle(e.target.value)}
              placeholder="標案主題關鍵字…"
              className="pl-8 pr-7"
            />
            {searchTitle && (
              <button onClick={() => setSearchTitle("")} className="absolute right-2.5 top-2.5 text-muted-foreground hover:text-foreground">
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          {(searchSchool || searchTitle || searchCategory) && (
            <span className="text-sm text-muted-foreground">
              找到 {displayedTenders.length} / {tenders.length} 筆
            </span>
          )}
        </div>

        {/* 第二行：分類 chips（橫向滾動） */}
        <div className="flex gap-1.5 overflow-x-auto pb-1 scrollbar-thin">
          <button
            onClick={() => setSearchCategory("")}
            className={`shrink-0 px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
              !searchCategory
                ? "bg-primary text-primary-foreground border-primary"
                : "bg-background text-muted-foreground border-border hover:border-primary hover:text-primary"
            }`}
          >
            所有分類
          </button>
          {(categories ?? []).map(c => (
            <button
              key={c.slug}
              onClick={() => setSearchCategory(searchCategory === c.slug ? "" : c.slug)}
              className={`shrink-0 px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                searchCategory === c.slug
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-background text-muted-foreground border-border hover:border-primary hover:text-primary"
              }`}
            >
              {c.nameChinese}
            </button>
          ))}
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12 cursor-pointer select-none hover:bg-muted/50" onClick={() => handleSort("id")}>
                  <div className="flex items-center">ID <SortIcon col="id" sortCol={sortCol} sortDir={sortDir} /></div>
                </TableHead>
                <TableHead className="cursor-pointer select-none hover:bg-muted/50" onClick={() => handleSort("title")}>
                  <div className="flex items-center">標題 <SortIcon col="title" sortCol={sortCol} sortDir={sortDir} /></div>
                </TableHead>
                <TableHead className="cursor-pointer select-none hover:bg-muted/50" onClick={() => handleSort("schoolName")}>
                  <div className="flex items-center">學校名稱 <SortIcon col="schoolName" sortCol={sortCol} sortDir={sortDir} /></div>
                </TableHead>
                <TableHead className="cursor-pointer select-none hover:bg-muted/50" onClick={() => handleSort("district")}>
                  <div className="flex items-center">地區 <SortIcon col="district" sortCol={sortCol} sortDir={sortDir} /></div>
                </TableHead>
                <TableHead className="cursor-pointer select-none hover:bg-muted/50" onClick={() => handleSort("status")}>
                  <div className="flex items-center">狀態 <SortIcon col="status" sortCol={sortCol} sortDir={sortDir} /></div>
                </TableHead>
                <TableHead className="w-32 whitespace-nowrap cursor-pointer select-none hover:bg-muted/50" onClick={() => handleSort("deadlineDate")}>
                  <div className="flex items-center">截止日期 <SortIcon col="deadlineDate" sortCol={sortCol} sortDir={sortDir} /></div>
                </TableHead>
                <TableHead>分類</TableHead>
                <TableHead className="w-24">操作</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">載入中...</TableCell></TableRow>
              ) : isError ? (
                <TableRow><TableCell colSpan={8} className="text-center py-8 text-red-500">
                  載入失敗：{(error as Error)?.message ?? "伺服器錯誤，請重新整理"}
                </TableCell></TableRow>
              ) : displayedTenders.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                  {searchSchool ? `找不到「${searchSchool}」的招標項目` : "暫無招標項目"}
                </TableCell></TableRow>
              ) : displayedTenders.map(t => {
                const todayHKStr = new Date().toLocaleDateString("sv-SE", { timeZone: "Asia/Hong_Kong" });
                const createdHKStr = t.createdAt
                  ? new Date(t.createdAt).toLocaleDateString("sv-SE", { timeZone: "Asia/Hong_Kong" })
                  : null;
                const isNewToday = createdHKStr === todayHKStr;
                return (
                <TableRow key={t.id} className={isNewToday ? "bg-amber-50 hover:bg-amber-100" : undefined}>
                  <TableCell className="text-muted-foreground text-xs">{t.id}</TableCell>
                  <TableCell className="font-medium max-w-[180px] truncate">
                    {isNewToday && <Badge className="mr-1.5 bg-amber-500 text-white text-[10px] px-1.5 py-0 h-4">新</Badge>}
                    {t.title}
                  </TableCell>
                  <TableCell className="text-sm max-w-[160px] truncate">{t.schoolName || <span className="text-muted-foreground">—</span>}</TableCell>
                  <TableCell className="text-sm">{t.district}</TableCell>
                  <TableCell>
                    <Badge className={t.status === "open" ? "bg-primary/10 text-primary border-primary/20" : "bg-red-50 text-red-600 border-red-300"}>
                      {t.status === "open" ? "招標中" : "已截止"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm font-medium whitespace-nowrap">
                    {t.deadlineDate}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {t.categories.map(c => <Badge key={c.slug} variant="outline" className="text-xs">{c.nameChinese}</Badge>)}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(t)}><Pencil className="h-3.5 w-3.5" /></Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500 hover:text-red-600" onClick={() => setDeleteId(t.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {editing ? "編輯招標" : "新增招標"}
              {editing && (() => {
                const idx = displayedTenders.findIndex(t => t.id === editing.id);
                return idx !== -1 ? (
                  <span className="text-xs font-normal text-muted-foreground">
                    {idx + 1} / {displayedTenders.length}
                  </span>
                ) : null;
              })()}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">

            {/* 標題 */}
            <div className="col-span-2 space-y-1.5">
              <Label>標題 <span className="text-red-500">*</span></Label>
              <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="招標標題" />
            </div>

            {/* 學校名稱（搜尋自動填地區） */}
            <div className="col-span-2 space-y-1.5">
              <Label>學校名稱 <span className="text-xs text-muted-foreground font-normal">（搜尋可自動填入地區及聯絡資料）</span></Label>
              <SchoolSearch value={form.schoolName ?? ""} onSelect={handleSchoolSelect} />
            </div>

            {/* 地區 + 狀態 */}
            <div className="space-y-1.5">
              <Label>地區 <span className="text-red-500">*</span></Label>
              <Select value={form.district} onValueChange={v => setForm(f => ({ ...f, district: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{DISTRICTS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>狀態 <span className="text-red-500">*</span></Label>
              <Select value={form.status} onValueChange={v => setForm(f => ({ ...f, status: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="open">招標中</SelectItem>
                  <SelectItem value="closed">已截止</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* 截止日期 + 截止時間 */}
            <div className="space-y-1.5">
              <Label>截止日期 <span className="text-red-500">*</span></Label>
              <SmartDateInput
                value={form.deadlineDate}
                onChange={v => setForm(f => ({ ...f, deadlineDate: v }))}
              />
              <p className="text-xs text-muted-foreground">可直接輸入 8 位數字，例：20260102</p>
            </div>
            <div className="space-y-1.5">
              <Label>截止時間 <span className="text-xs text-muted-foreground font-normal">（選填）</span></Label>
              <SmartTimeInput
                value={form.deadlineTime ?? ""}
                onChange={v => setForm(f => ({ ...f, deadlineTime: v }))}
              />
              <p className="text-xs text-muted-foreground">可直接輸入 4 位數字，例：1330，或從下拉選擇每半小時</p>
            </div>

            {/* 文件連結（最多 10 個）*/}
            <div className="col-span-2 space-y-1.5">
              <Label>參考連結 <span className="text-xs text-muted-foreground font-normal">（學校網頁內顯示此招標標題的頁面）</span></Label>
              <Input
                value={form.referenceUrl ?? ""}
                onChange={e => setForm(f => ({ ...f, referenceUrl: e.target.value }))}
                placeholder="https://..."
              />
              <p className="text-xs text-muted-foreground">如同一學校有多條 directurl，請確認該頁確實顯示這個招標標題後再填入。</p>
            </div>

            {/* 文件連結（最多 10 個）*/}
            <div className="space-y-2">
              <Label>文件連結 <span className="text-xs text-muted-foreground font-normal">（最多 10 個）</span></Label>
              {(form.documentUrls as string[] ?? EMPTY_DOC_URLS()).map((url, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground w-5 text-right shrink-0">{idx + 1}.</span>
                  <Input
                    value={url}
                    onChange={e => {
                      const next = [...(form.documentUrls as string[])];
                      next[idx] = e.target.value;
                      setForm(f => ({ ...f, documentUrls: next }));
                    }}
                    placeholder="https://..."
                  />
                </div>
              ))}
            </div>

            {/* 描述 */}
            <div className="col-span-2 space-y-1.5">
              <Label>描述</Label>
              <Textarea rows={3} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="招標描述" />
            </div>

            {/* 聯絡資料分隔線 */}
            <div className="col-span-2 flex items-center gap-3 pt-1">
              <div className="h-px flex-1 bg-border" />
              <span className="text-xs text-muted-foreground">聯絡資料</span>
              <div className="h-px flex-1 bg-border" />
            </div>

            {/* 聯絡人 */}
            <div className="space-y-1.5">
              <Label>聯絡人</Label>
              <Input value={form.contactPerson} onChange={e => setForm(f => ({ ...f, contactPerson: e.target.value }))} placeholder="負責人姓名" />
            </div>

            {/* 聯絡電話 */}
            <div className="space-y-1.5">
              <Label>聯絡電話</Label>
              <Input value={form.contactPhone} onChange={e => setForm(f => ({ ...f, contactPhone: e.target.value }))} placeholder="例：2345 6789" />
            </div>

            {/* 聯絡電郵 */}
            <div className="space-y-1.5">
              <Label>聯絡電郵</Label>
              <Input type="email" value={form.contactEmail} onChange={e => setForm(f => ({ ...f, contactEmail: e.target.value }))} placeholder="info@school.edu.hk" />
            </div>

            {/* 舊聯絡資料欄（保留相容） */}
            <div className="space-y-1.5">
              <Label>其他聯絡資料</Label>
              <Input value={form.contactInfo} onChange={e => setForm(f => ({ ...f, contactInfo: e.target.value }))} placeholder="補充聯絡方式" />
            </div>

            {/* 聯絡地址 */}
            <div className="col-span-2 space-y-1.5">
              <Label>聯絡地址</Label>
              <Input value={form.contactAddress} onChange={e => setForm(f => ({ ...f, contactAddress: e.target.value }))} placeholder="學校地址" />
            </div>

            {/* 分類 */}
            <div className="col-span-2 space-y-2">
              <Label>分類</Label>
              <div className="flex flex-wrap gap-2">
                {categories?.map(cat => (
                  <button key={cat.slug} type="button" onClick={() => toggleCat(cat.slug)}
                    className={`px-3 py-1 text-xs rounded-full border transition-all ${form.categories.includes(cat.slug) ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:border-primary/40"}`}>
                    {cat.nameChinese}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter className="flex-wrap gap-2">
            <Button variant="outline" onClick={() => setOpen(false)} disabled={save.isPending}>取消</Button>
            {editing && getNextTender() && (
              <Button
                variant="outline"
                onClick={handleSaveAndNext}
                disabled={save.isPending || !form.title || !form.deadlineDate}
                className="border-primary/40 text-primary hover:bg-primary/5"
              >
                {save.isPending && saveMode.current === "saveAndNext" ? "儲存中..." : "儲存並轉下一項目"}
              </Button>
            )}
            <Button
              onClick={() => { saveMode.current = "save"; save.mutate({ ...form, ...(editing ? { id: editing.id } : {}) }); }}
              disabled={save.isPending || !form.title || !form.deadlineDate}
            >
              {save.isPending && saveMode.current === "save" ? "儲存中..." : "儲存"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm Dialog */}
      <Dialog open={deleteId !== null} onOpenChange={() => setDeleteId(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>確認刪除</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">此操作無法復原，確定要刪除這個招標項目嗎？</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteId(null)}>取消</Button>
            <Button variant="destructive" onClick={() => deleteId !== null && remove.mutate(deleteId)} disabled={remove.isPending}>
              {remove.isPending ? "刪除中..." : "確認刪除"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* JSON Import Dialog */}
      <Dialog open={jsonDialogOpen} onOpenChange={v => { if (!v) closeJsonDialog(); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardPaste className="h-5 w-5 text-primary" />
              貼上 JSON 匯入招標
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 min-h-0 flex flex-col gap-3 overflow-y-auto pr-0.5">
            {/* Format hint */}
            <div className="bg-muted/50 rounded-md p-3 text-xs text-muted-foreground">
              <p className="font-medium text-foreground mb-1">支援三種格式：</p>
              <div className="grid grid-cols-3 gap-2 h-[210px]">
                <div>
                  <p className="text-[10px] font-medium text-foreground/70 mb-0.5">① 多學校格式（新）</p>
                  <pre className="h-[190px] overflow-auto whitespace-pre-wrap text-[10px] leading-relaxed bg-background/60 rounded p-1.5">{`{
  "total_schools": 10,
  "total_new": 8,
  "schools": [
    {
      "school_name": "某某學校",
      "district": "元朗區",
      "tender_urls": ["https://school.edu.hk/tenders"],
      "items": [
        {
          "title": "...",
          "referenceUrl": "https://school.edu.hk/tender/123",
          "categories": ["engineering"]
        }
      ]
    }
  ]
}`}</pre>
                </div>
                <div>
                  <p className="text-[10px] font-medium text-foreground/70 mb-0.5">② 單學校爬蟲格式</p>
                  <pre className="h-[190px] overflow-auto whitespace-pre-wrap text-[10px] leading-relaxed bg-background/60 rounded p-1.5">{`{
  "school_name": "金錢村何東學校",
  "school_no": "512257000123",
  "tender_url": "https://school.edu.hk/tenders",
  "exported_at": "2026-04-29T20:52:57",
  "total": 68,
  "records": [
    {
      "title": "校務處內外改善工程",
      "deadlineDate": "2026-05-26",
      "schoolName": "金錢村何東學校",
      "contactPhone": "26703849",
      "referenceUrl": "https://school.edu.hk/tender/123",
      "documentUrls": ["https://..."],
      "categories": []
    }
  ]
}`}</pre>
                </div>
                <div>
                  <p className="text-[10px] font-medium text-foreground/70 mb-0.5">③ 簡單陣列格式</p>
                  <pre className="h-[190px] overflow-auto whitespace-pre-wrap text-[10px] leading-relaxed bg-background/60 rounded p-1.5">{`[
  {
    "title": "學校冷氣保養服務",
    "district": "荃灣區",
    "status": "open",
    "deadlineDate": "2026-06-30",
    "deadlineTime": "17:00",
    "schoolName": "荃灣某中學",
    "contactPerson": "陳先生",
    "contactPhone": "2345-6789",
    "referenceUrl": "https://school.edu.hk/tender/123",
    "documentUrls": ["https://..."],
    "categories": ["engineering"]
  }
]`}</pre>
                </div>
              </div>
            </div>

            {/* Textarea */}
            <div className="flex flex-col gap-1.5">
              <Label>貼上 JSON 內容</Label>
              <Textarea
                value={jsonText}
                onChange={e => handleJsonTextChange(e.target.value)}
                placeholder='貼上爬蟲匯出 JSON 或直接陣列...'
                className="h-44 font-mono text-xs resize-none"
              />
            </div>

            {/* Parse status */}
            {jsonParseError && (
              <div className="flex items-start gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-3">
                <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                <span>{jsonParseError}</span>
              </div>
            )}
            {jsonParsed && !jsonParseError && !jsonResult && (
              <div className="bg-primary/5 border border-primary/20 rounded-md p-3 space-y-1.5">
                <div className="flex items-center gap-2 text-sm text-primary font-medium">
                  <CheckCircle2 className="h-4 w-4 shrink-0" />
                  {jsonMeta?.isMultiSchool
                    ? <>多學校格式：<strong>{jsonMeta.totalSchools}</strong> 間學校，共 <strong>{jsonParsed.length}</strong> 筆招標</>
                    : <>JSON 格式正確，共 <strong>{jsonParsed.length}</strong> 筆招標</>
                  }
                </div>
                {jsonMeta?.isMultiSchool && jsonMeta.schoolSummary && jsonMeta.schoolSummary.length > 0 && (
                  <div className="pl-6 space-y-0.5">
                    {jsonMeta.exportedAt && (
                      <p className="text-xs text-muted-foreground">📅 {jsonMeta.exportedAt.replace("T", " ").slice(0, 16)}</p>
                    )}
                    <div className="text-xs text-muted-foreground max-h-24 overflow-y-auto space-y-0.5">
                      {jsonMeta.schoolSummary.map((s, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <span className="text-primary font-medium">{s.count} 筆</span>
                          <span>{s.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {jsonMeta && !jsonMeta.isMultiSchool && (
                  <div className="text-xs text-muted-foreground grid grid-cols-2 gap-x-4 gap-y-0.5 pl-6">
                    {jsonMeta.schoolName && <span>🏫 {jsonMeta.schoolName}</span>}
                    {jsonMeta.schoolNo && <span>🔢 {jsonMeta.schoolNo}</span>}
                    {jsonMeta.exportedAt && <span>📅 {jsonMeta.exportedAt.replace("T", " ").slice(0, 16)}</span>}
                    {jsonMeta.tenderUrl && (
                      <a href={jsonMeta.tenderUrl} target="_blank" rel="noopener noreferrer"
                        className="text-primary underline truncate col-span-2">
                        🔗 {jsonMeta.tenderUrl}
                      </a>
                    )}
                    {jsonMeta.total != null && jsonMeta.total !== jsonParsed.length && (
                      <span className="col-span-2 text-amber-600">⚠️ 檔案標示共 {jsonMeta.total} 筆，但解析到 {jsonParsed.length} 筆</span>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Import result */}
            {jsonResult && (
              <div className={`rounded-md border p-3 text-sm ${jsonResult.errors.length > 0 ? "bg-amber-50 border-amber-300" : "bg-green-50 border-green-300"}`}>
                <div className="flex items-center gap-2 font-medium mb-1">
                  {jsonResult.errors.length === 0
                    ? <><CheckCircle2 className="h-4 w-4 text-green-600" /> 全部匯入成功！</>
                    : <><AlertCircle className="h-4 w-4 text-amber-600" /> 部分成功</>
                  }
                </div>
                <p>成功匯入 <strong>{jsonResult.inserted}</strong> / {jsonResult.total} 筆</p>
                {jsonResult.errors.length > 0 && (
                  <ul className="mt-1.5 text-xs text-amber-700 space-y-0.5 max-h-28 overflow-y-auto">
                    {jsonResult.errors.map((e, i) => <li key={i}>• {e}</li>)}
                  </ul>
                )}
              </div>
            )}
          </div>

          <DialogFooter className="pt-2">
            <Button variant="outline" onClick={closeJsonDialog}>
              {jsonResult ? "關閉" : "取消"}
            </Button>
            {!jsonResult && (
              <Button
                onClick={handleJsonImport}
                disabled={!jsonParsed || jsonParsed.length === 0 || jsonImporting}
              >
                {jsonImporting ? "匯入中..." : `匯入 ${jsonParsed?.length ?? 0} 筆招標`}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
